import { Injectable, inject } from '@angular/core';
import { HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Auth, getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { BackendService } from '@shared/services';
import {
  AuthTokenResponse,
  ExpiredPasswordProps,
  LinkedBusinessAccount,
  LinkedBusinessAccounts,
  LoginTenantResponse,
} from '@shared/models';
import { environment } from '@env';
import { constants, MESSAGE } from '@shared/constants';
import { SKIP_ERROR_NOTIFICATION } from '@shared/interceptors';
import { APP_ENV_CONFIG } from '../app.config';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  envConfig = inject(APP_ENV_CONFIG);

  afAuth?: Auth;

  backendService = inject(BackendService);

  // Login - Step 0
  public getAuthTenantInfo(): Observable<LoginTenantResponse> {
    const realm = constants.DEFAULT_REALM_NAME;
    return this.backendService.get<LoginTenantResponse>(`${environment.uaaService}/${realm}/tenant-info`);
  }

  // Maintenance API
  public getMaintenanceInfo(): Observable<string> {
    return this.backendService.get<string>('/status', { responseType: 'text' });
  }

  //  Login - Step 1 - FIREBASE | GCP_IDENTITY_PLATFORM
  public googleCloudLogin(email: string, password: string, tenantId?: string): Observable<{ userId: string; token: string }> {
    const app = initializeApp(this.envConfig.firebase);
    this.afAuth = getAuth(app);
    this.afAuth.tenantId = tenantId || null;

    return from(signInWithEmailAndPassword(this.afAuth, email, password)).pipe(
      catchError(() => throwError(() => new Error(MESSAGE.LOGIN_ERROR))),
      switchMap(() => {
        if (this.afAuth?.currentUser) {
          const userId = this.afAuth.currentUser.uid;
          return from(this.afAuth.currentUser.getIdToken()).pipe(
            map((token) => ({
              userId,
              token,
            })),
          );
        } else {
          return throwError(() => new Error(MESSAGE.UNKNOWN_ERROR));
        }
      }),
    );
  }

  /**
   * Login - Step 2 - KEYCLOAK | FIREBASE | GCP_IDENTITY_PLATFORM
   * API {{base-url}}/uaa/api/auth/{{tenant}}/exchange-token
   *
   * @param accessToken id token from auth provider KEYCLOAK | FIREBASE | GCP_IDENTITY_PLATFORM
   * @param [tenant] realm name from user input
   * @returns {Observable<AuthTokenResponse>}
   */
  public exchangeToken(accessToken: string): Observable<AuthTokenResponse> {
    const realm = constants.DEFAULT_REALM_NAME;
    const headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`);

    return this.backendService.post<AuthTokenResponse>(
      `${environment.uaaService}/${realm}/exchange-token`,
      { externalToken: accessToken },
      { headers },
    );
  }

  // Refresh token
  public refreshToken(businessAccountId: string): Observable<AuthTokenResponse> {
    const realm = constants.DEFAULT_REALM_NAME;
    return this.backendService.post(`${environment.uaaService}/${realm}/business-accounts/${businessAccountId}/token`);
  }

  // Logout
  public logout(): Observable<boolean> {
    return this.backendService.post(`${environment.uaaService}/${constants.DEFAULT_REALM_NAME}/logout`).pipe(
      tap({ next: () => this.afAuth?.signOut() }),
      map(() => true),
      catchError(() => of(true)),
    );
  }

  public forgotPassword(email: string): Observable<void> {
    return this.backendService
      .post(`${environment.accountFlowEndpoint}/auth/realms/${constants.DEFAULT_REALM_NAME}/forgot-password`, { email })
      .pipe(map(() => undefined));
  }

  public getLinkedBusinessAccounts(): Observable<LinkedBusinessAccount[]> {
    const params = new HttpParams().set('page', '0').set('size', '10');

    return this.backendService
      .get<LinkedBusinessAccounts>(`/v1/employees/self/linked-business-accounts`, { params })
      .pipe(map((response) => response?.content ?? []));
  }

  /**
   * PCI recovery flow: sets a new password after login was blocked by expiry.
   * Unauthenticated (no session cookies) — authorized solely by the fresh provider
   * token from the sign-in just performed. Skips the global error notification so the
   * caller can surface field-level (400) and stale-token (403) errors on the screen.
   */
  public expiredPassword(expiredPasswordData: ExpiredPasswordProps, realm = constants.DEFAULT_REALM_NAME): Observable<void> {
    return this.backendService.post<void>(`${environment.uaaService}/${realm}/expired-password`, expiredPasswordData, {
      context: new HttpContext().set(SKIP_ERROR_NOTIFICATION, true),
    });
  }

  /**
   * Re-mints a fresh provider token by re-authenticating with the current (expired) password.
   * Used by the expiry recovery screen when the original token has gone stale (KOR-14853).
   */
  public getReauthProviderToken(email: string, currentPassword: string): Observable<string> {
    return this.getAuthTenantInfo().pipe(
      switchMap(({ providerTenantId }) => {
        const app = getApps().length ? getApp() : initializeApp(this.envConfig.firebase);
        this.afAuth = getAuth(app);
        this.afAuth.tenantId = providerTenantId || null;
        return from(signInWithEmailAndPassword(this.afAuth, email, currentPassword));
      }),
      switchMap((credential) => from(credential.user.getIdToken())),
      catchError((error) => {
        const errorCode = (error as { code?: string })?.code;
        const isWrongPassword = errorCode === 'auth/wrong-password' || errorCode === 'auth/invalid-credential';
        return throwError(() => new Error(isWrongPassword ? MESSAGE.CURRENT_PASSWORD_INCORRECT : MESSAGE.UNKNOWN_ERROR));
      }),
    );
  }
}
